import { toast } from 'react-toastify';
import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    getTenantProfile, getTenantProfileSuccess, getTenantProfileFail,
    getPublicTenantProfile, getPublicTenantProfileSuccess, getPublicTenantProfileFail,
    saveTenantProfile, saveTenantProfileSuccess, saveTenantProfileFail,
    revokeProfileSlug, revokeProfileSlugSuccess, revokeProfileSlugFail,
    regenerateProfileSlug, regenerateProfileSlugSuccess, regenerateProfileSlugFail
} from '../actions/tenantProfile';
import TenantProfileApi from '../services/tenantProfile';
import { uploadFileToS3 } from '../services/storage/s3';
import { selectUserId } from '../selectors/users';
import i18n from '../translations/i18n';
import config from '../config/config';
import { Action } from '../reducers/types';

interface SubmitParameters {
    payload: {
        formData: any;
        resolve: any;
        reject: any;
    };
}

function* handleGetTenantProfile(): SagaIterator {
    try {
        const userId: string = yield select(selectUserId);
        const profile = yield call(TenantProfileApi.getOwnProfile, userId);
        yield put(getTenantProfileSuccess(profile));
    } catch (error: any) {
        if (error?.response?.status === 404) {
            yield put(getTenantProfileSuccess(null));
        } else {
            yield put(getTenantProfileFail(error?.message || 'Failed to load profile'));
        }
    }
}

function* handleGetPublicTenantProfile({ payload: slug }: Action<string>): SagaIterator {
    try {
        const profile = yield call(TenantProfileApi.getPublicProfile, slug);
        yield put(getPublicTenantProfileSuccess(profile));
    } catch (error: any) {
        yield put(getPublicTenantProfileFail(error?.message || 'Profile not found'));
    }
}

function* handleSaveTenantProfile({ payload }: SubmitParameters): SagaIterator {
    const { formData, resolve, reject } = payload;
    try {
        const userId: string = yield select(selectUserId);
        const profileData = { ...formData };

        if (profileData.profilePicture instanceof Blob) {
            const bucket = config.s3.profilePictures.BUCKET;
            const region = config.s3.profilePictures.REGION;
            const result: { key: string } = yield call(uploadFileToS3, profileData.profilePicture, bucket, region);
            profileData.profilePictureKey = result.key;
            delete profileData.profilePicture;
        }

        const profile = yield call(TenantProfileApi.saveProfile, userId, profileData);
        yield put(saveTenantProfileSuccess(profile));
        toast.info(i18n.t('tenantProfile.savedSuccess') as string);
        if (resolve) resolve();
    } catch (error: any) {
        yield put(saveTenantProfileFail(error?.message || 'Failed to save profile'));
        toast.error(i18n.t('tenantProfile.saveFailed') as string);
        if (reject) reject();
    }
}

function* handleRevokeSlug(): SagaIterator {
    try {
        const userId: string = yield select(selectUserId);
        yield call(TenantProfileApi.revokeSlug, userId);
        yield put(revokeProfileSlugSuccess());
        toast.info(i18n.t('tenantProfile.linkRevoked') as string);
    } catch (error: any) {
        yield put(revokeProfileSlugFail(error?.message || 'Failed to revoke link'));
    }
}

function* handleRegenerateSlug(): SagaIterator {
    try {
        const userId: string = yield select(selectUserId);
        const result = yield call(TenantProfileApi.regenerateSlug, userId);
        yield put(regenerateProfileSlugSuccess(result));
        toast.info(i18n.t('tenantProfile.linkGenerated') as string);
    } catch (error: any) {
        yield put(regenerateProfileSlugFail(error?.message || 'Failed to generate link'));
    }
}

function* watchTenantProfileActions(): SagaIterator {
    yield all([
        takeLatest(getTenantProfile, handleGetTenantProfile),
        takeLatest(getPublicTenantProfile, handleGetPublicTenantProfile),
        takeLatest(saveTenantProfile, handleSaveTenantProfile),
        takeLatest(revokeProfileSlug, handleRevokeSlug),
        takeLatest(regenerateProfileSlug, handleRegenerateSlug)
    ]);
}

export default [watchTenantProfileActions];
